'use client';
import { useAriaLabels } from '@/hooks/useAriaLabels';

import { useImageLoader } from '@/hooks/useImageLoader';
import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useRef, useState } from 'react';
import styles from '../HeroSlider.module.scss';
import {
  createHeroThumbnailLoader,
  HERO_THUMBNAIL_DIMENSIONS,
  HERO_THUMBNAIL_SIZES,
} from '../HeroSlider.constants';
import type { ThumbnailStripProps } from '../HeroSlider.types';
import { getPaddingSlides, getRotatedOrder, parseLineBreaks } from '../HeroSlider.utils';

/**
 * ThumbnailStrip - Portrait-cropped thumbnail navigation for Dynamic Hero
 *
 * Thumbnails rotate like a queue: the active slide's thumbnail is always at the
 * trailing (right) end, and the rest follow in sequence.
 *
 * On click, the clicked thumbnail and the one before it fade out and collapse
 * (ease-in-out) while the remaining thumbnails slide left to fill the gap.
 * The main slide transitions simultaneously.
 *
 * Non-interactive padding thumbnails are appended after the active slide to
 * fill the overflow area, preventing re-entering thumbnails from becoming
 * visible at the strip's trailing edge.
 */
export const ThumbnailStrip = ({
  slides,
  activeIndex,
  onSelect,
  testId,
  stripRef,
  enteringIndex,
  enteringVisible,
  fadingOutIndex,
}: ThumbnailStripProps) => {
  const aria = useAriaLabels();
  const { loader } = useImageLoader();
  const [exitingIndices, setExitingIndices] = useState<Set<number>>(new Set());
  const [frozenOrder, setFrozenOrder] = useState<number[] | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);

  const computedOrder = getRotatedOrder(slides.length, activeIndex);
  const displayOrder = frozenOrder ?? computedOrder;

  const activeInStrip = displayOrder[displayOrder.length - 1];
  const paddingSlides = getPaddingSlides(slides.length, activeInStrip);

  const handleThumbnailClick = useCallback(
    (slideIndex: number, _visualPosition: number, event: React.MouseEvent<HTMLButtonElement>) => {
      if (slideIndex === activeIndex) return;

      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setFrozenOrder(null);
      setExitingIndices(new Set());

      const currentOrder = getRotatedOrder(slides.length, activeIndex);
      setFrozenOrder(currentOrder);

      const positionInOrder = currentOrder.indexOf(slideIndex);
      const newExiting = new Set<number>();
      for (let i = 0; i <= positionInOrder; i++) {
        newExiting.add(currentOrder[i]);
      }
      setExitingIndices(newExiting);

      const rect = event.currentTarget.getBoundingClientRect();
      onSelect(slideIndex, rect);

      const exitingSnapshot = new Set(newExiting);
      resetTimerRef.current = setTimeout(() => {
        setFrozenOrder((prev) => (prev ? prev.filter((i) => !exitingSnapshot.has(i)) : null));
        setExitingIndices(new Set());

        rafRef.current = requestAnimationFrame(() => {
          setFrozenOrder(null);
          rafRef.current = null;
          resetTimerRef.current = null;
        });
      }, 750);
    },
    [activeIndex, slides.length, onSelect]
  );

  return (
    <div
      ref={stripRef}
      className={styles.thumbnailStrip}
      role="tablist"
      aria-label={aria.action('slideThumbnails')}
      data-testid={testId('thumbnails')}
    >
      {displayOrder.map((slideIndex, visualPosition) => {
        const slide = slides[slideIndex];
        if (!slide) return null;
        const desktopUrl = slide.media?.desktop?.url;
        const mobileUrl = slide.media?.mobile?.url;
        const desktopFocal = slide.media?.desktop?.focalPoint;
        const mobileFocal = slide.media?.mobile?.focalPoint;
        const desktopWidth = slide.media?.desktop?.width ?? undefined;
        const desktopHeight = slide.media?.desktop?.height ?? undefined;
        const mobileWidth = slide.media?.mobile?.width ?? undefined;
        const mobileHeight = slide.media?.mobile?.height ?? undefined;
        const desktopPosition = desktopFocal ? `${desktopFocal.x}% ${desktopFocal.y}%` : 'center';
        const mobilePosition = mobileFocal ? `${mobileFocal.x}% ${mobileFocal.y}%` : 'center';
        const hasSeparateMobile = mobileUrl && mobileUrl !== desktopUrl;
        const { heading, subheading, subtitleAbove } = slide;
        const isActive = slideIndex === activeIndex;
        const isExiting = exitingIndices.has(slideIndex);
        const isEntering = slideIndex === enteringIndex;
        const isFadingOut = slideIndex === fadingOutIndex;
        const alt = heading || `Slide ${slideIndex + 1}`;

        return (
          <button
            key={slide.sys?.id || slideIndex}
            type="button"
            role="tab"
            className={clsx(
              styles.thumbnail,
              isActive && styles.thumbnailActive,
              isExiting && styles.thumbnailExiting,
              isFadingOut && styles.thumbnailFadingOut,
              isEntering && styles.thumbnailEntering,
              isEntering && enteringVisible && styles.thumbnailEnteringVisible
            )}
            onClick={(e) => handleThumbnailClick(slideIndex, visualPosition, e)}
            aria-label={`Go to slide ${slideIndex + 1}: ${heading || ''}`}
            aria-selected={isActive}
            data-slide-index={slideIndex}
            data-testid={testId(`thumb-${slideIndex}`)}
          >
            {hasSeparateMobile && (
              <Image
                src={mobileUrl}
                alt={alt}
                fill
                className={clsx(styles.thumbnailImage, styles.mobileOnly)}
                sizes={HERO_THUMBNAIL_SIZES.mobileOnly}
                loader={createHeroThumbnailLoader(
                  loader,
                  HERO_THUMBNAIL_DIMENSIONS.mobile,
                  mobileFocal,
                  mobileWidth && mobileHeight ? { x: mobileWidth, y: mobileHeight } : undefined
                )}
                style={{ objectPosition: mobilePosition }}
              />
            )}
            {desktopUrl && (
              <Image
                src={desktopUrl}
                alt={alt}
                fill
                className={clsx(styles.thumbnailImage, hasSeparateMobile && styles.desktopOnly)}
                sizes={
                  hasSeparateMobile
                    ? HERO_THUMBNAIL_SIZES.desktopOnly
                    : HERO_THUMBNAIL_SIZES.responsive
                }
                loader={createHeroThumbnailLoader(
                  loader,
                  HERO_THUMBNAIL_DIMENSIONS.desktop,
                  desktopFocal,
                  desktopWidth && desktopHeight ? { x: desktopWidth, y: desktopHeight } : undefined
                )}
                style={{ objectPosition: desktopPosition }}
              />
            )}
            {(subtitleAbove || subheading || heading) && (
              <div className={styles.thumbnailContent}>
                {(subtitleAbove || subheading) && (
                  <p className={styles.thumbnailSubtitleAbove}>
                    {parseLineBreaks(subtitleAbove || subheading || '')}
                  </p>
                )}
                {heading && <p className={styles.thumbnailHeading}>{parseLineBreaks(heading)}</p>}
              </div>
            )}
          </button>
        );
      })}

      {paddingSlides.map((slideIndex, i) => {
        const slide = slides[slideIndex];
        if (!slide) return null;
        const desktopUrl = slide.media?.desktop?.url;
        const mobileUrl = slide.media?.mobile?.url;
        const desktopFocal = slide.media?.desktop?.focalPoint;
        const mobileFocal = slide.media?.mobile?.focalPoint;
        const desktopWidth = slide.media?.desktop?.width ?? undefined;
        const desktopHeight = slide.media?.desktop?.height ?? undefined;
        const mobileWidth = slide.media?.mobile?.width ?? undefined;
        const mobileHeight = slide.media?.mobile?.height ?? undefined;
        const desktopPosition = desktopFocal ? `${desktopFocal.x}% ${desktopFocal.y}%` : 'center';
        const mobilePosition = mobileFocal ? `${mobileFocal.x}% ${mobileFocal.y}%` : 'center';
        const hasSeparateMobile = mobileUrl && mobileUrl !== desktopUrl;
        const { heading, subheading, subtitleAbove } = slide;

        return (
          <div key={`pad-${i}`} className={styles.thumbnail} aria-hidden="true">
            {hasSeparateMobile && (
              <Image
                src={mobileUrl}
                alt=""
                fill
                className={clsx(styles.thumbnailImage, styles.mobileOnly)}
                sizes={HERO_THUMBNAIL_SIZES.mobileOnly}
                loader={createHeroThumbnailLoader(
                  loader,
                  HERO_THUMBNAIL_DIMENSIONS.mobile,
                  mobileFocal,
                  mobileWidth && mobileHeight ? { x: mobileWidth, y: mobileHeight } : undefined
                )}
                style={{ objectPosition: mobilePosition }}
              />
            )}
            {desktopUrl && (
              <Image
                src={desktopUrl}
                alt=""
                fill
                className={clsx(styles.thumbnailImage, hasSeparateMobile && styles.desktopOnly)}
                sizes={
                  hasSeparateMobile
                    ? HERO_THUMBNAIL_SIZES.desktopOnly
                    : HERO_THUMBNAIL_SIZES.responsive
                }
                loader={createHeroThumbnailLoader(
                  loader,
                  HERO_THUMBNAIL_DIMENSIONS.desktop,
                  desktopFocal,
                  desktopWidth && desktopHeight ? { x: desktopWidth, y: desktopHeight } : undefined
                )}
                style={{ objectPosition: desktopPosition }}
              />
            )}
            {(subtitleAbove || subheading || heading) && (
              <div className={styles.thumbnailContent}>
                {(subtitleAbove || subheading) && (
                  <p className={styles.thumbnailSubtitleAbove}>
                    {parseLineBreaks(subtitleAbove || subheading || '')}
                  </p>
                )}
                {heading && <p className={styles.thumbnailHeading}>{parseLineBreaks(heading)}</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
